import { YES_NO, applicantSection, feeInfo, otherAttachments, replySection } from "modules/ebiForms/common";
import {
  IN_OWN_NAME,
  ON_BEHALF,
  socialApplicantRoleSection,
  socialRoleAttachments,
} from "modules/ebiForms/definitions/sharedSocial";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * C2. Wniosek o skierowanie do domu pomocy społecznej i ustalenie opłaty za pobyt — art. 54, 59–61 ustawy z 12.03.2004 r.
 * o pomocy społecznej, § 8 rozporządzenia MPiPS z 23.08.2012 r. w sprawie domów pomocy społecznej (t.j. Dz.U. z 2025 r.
 * poz. 51). Brak wzoru ogólnopolskiego; układ jak wzór MGOPS Złocieniec (https://mgops.zlocieniec.pl/images/pliki/wniosek-dps.pdf,
 * pobrany 22.10.2026: dane osoby, uzasadnienie, zgoda na ponoszenie i potrącanie odpłatności, załączniki medyczne),
 * z sekcją „kto składa” (osoba / przedstawiciel ustawowy / inna osoba za zgodą — art. 102) według „Uwag dla EBI”.
 * Załączniki z § 8 ust. 1–2 rozporządzenia; zaświadczenia lekarskie, psychiatry, psychologa i orzeczenie to praktyka ośrodków
 * (nieobowiązkowe, do potwierdzenia z ośrodkiem). Wywiad środowiskowy sporządza ośrodek, nie wnioskodawca.
 */
const COURT: EbiCondition = { field: "orzeczenieSadu", equals: "tak" };
const NOT_COURT: EbiCondition = { not: COURT };
const INCOME_DECISION: EbiCondition = {
  any: [
    { field: "dochodOsoby", includes: "zasilekStaly" },
    { field: "dochodOsoby", includes: "emerytura" },
  ],
};

const C2: IEbiFormDefinition = {
  code: "C2",
  version: 1,
  title: "Wniosek o skierowanie do domu pomocy społecznej",
  category: EEbiFormCategory.Social,
  office: EEbiFormOffice.MunicipalUnit,
  description: "Skierowanie do domu pomocy społecznej osoby wymagającej całodobowej opieki i ustalenie opłaty za pobyt.",
  keywords: ["dom pomocy społecznej", "dps", "skierowanie", "opłata za pobyt", "opieka całodobowa", "pomoc społeczna"],
  organ: "Wójt / Burmistrz / Prezydent Miasta (ośrodek pomocy społecznej) gminy właściwej według miejsca zamieszkania osoby kierowanej",
  legalBasis:
    "art. 54 ust. 1, art. 59 ust. 1 i art. 61 ustawy z dnia 12 marca 2004 r. o pomocy społecznej; § 8 rozporządzenia Ministra Pracy i Polityki Społecznej z dnia 23 sierpnia 2012 r. w sprawie domów pomocy społecznej",
  request: "Wnoszę o skierowanie do domu pomocy społecznej osoby wskazanej we wniosku i ustalenie opłaty za jej pobyt.",
  feeLabel: "wg decyzji",
  fee: "Bez opłaty skarbowej. Odpłatność za pobyt w domu ustala decyzja (art. 59–61 ustawy o pomocy społecznej).",
  deadlineLabel: "do miesiąca",
  deadline: "Decyzja po rodzinnym wywiadzie środowiskowym, do miesiąca (art. 35 KPA).",
  sections: [
    applicantSection(),
    socialApplicantRoleSection("osoby ubiegającej się o skierowanie"),
    {
      code: "skierowanie",
      title: "Skierowanie do domu pomocy społecznej",
      fields: [
        feeInfo(
          "infoSkierowanie",
          "Decyzję o skierowaniu i o opłacie wydaje gmina, a decyzję o umieszczeniu w domu — starosta powiatu prowadzącego dom (art. 59 ustawy). Przed decyzją pracownik socjalny przeprowadza rodzinny wywiad środowiskowy i ocenia, czy opieki nie można zapewnić w formie usług w miejscu zamieszkania.",
        ),
        {
          code: "orzeczenieSadu",
          type: "radio",
          label: "Czy skierowanie następuje na podstawie orzeczenia sądu opiekuńczego (art. 54 ust. 4 ustawy)?",
          required: true,
          defaultValue: "nie",
          options: YES_NO,
        },
        {
          code: "uzasadnienie",
          type: "textarea",
          label: "Uzasadnienie wniosku",
          required: true,
          maxLength: 5000,
          hint: "Wskaż przyczyny, z powodu których osoba ubiega się o umieszczenie w domu pomocy społecznej.",
        },
      ],
    },
    {
      code: "oplata",
      title: "Opłata za pobyt",
      visibleWhen: NOT_COURT,
      fields: [
        feeInfo(
          "infoOplata",
          "Opłatę za pobyt wnosi w pierwszej kolejności mieszkaniec domu (nie więcej niż 70% swojego dochodu), następnie małżonek, zstępni przed wstępnymi, a w ostatniej kolejności gmina (art. 61 ustawy). Dlatego do wniosku dołącza się oświadczenia o dochodach tych osób.",
        ),
        {
          code: "dochodOsoby",
          type: "checkboxes",
          label: "Osoba ubiegająca się o skierowanie otrzymuje",
          required: true,
          options: [
            { value: "zasilekStaly", label: "zasiłek stały" },
            { value: "emerytura", label: "emeryturę lub rentę (ZUS, KRUS)" },
            { value: "inny", label: "inny dochód" },
            { value: "brak", label: "nie ma dochodu" },
          ],
        },
        {
          code: "zgodaOplata",
          type: "declaration",
          label:
            "Wyrażam zgodę na ponoszenie kosztu odpłatności za pobyt w domu pomocy społecznej oraz zgadzam się na potrącanie odpłatności z moich dochodów.",
          required: true,
          visibleWhen: IN_OWN_NAME,
        },
        {
          code: "kombatant",
          type: "radio",
          label:
            "Czy osoba ubiegająca się o skierowanie ma uprawnienia z ustawy o kombatantach oraz niektórych osobach będących ofiarami represji wojennych i okresu powojennego?",
          required: true,
          defaultValue: "nie",
          options: YES_NO,
        },
      ],
    },
    replySection(),
  ],
  attachments: [
    ...socialRoleAttachments(),
    {
      code: "postanowienieSadu",
      label: "Orzeczenie sądu opiekuńczego o umieszczeniu w domu pomocy społecznej",
      hint: "Przy skierowaniu na podstawie orzeczenia sądu pozostałych dokumentów z § 8 ust. 2 rozporządzenia nie wymaga się.",
      visibleWhen: COURT,
      required: true,
    },
    {
      code: "oswiadczeniaDochody",
      label: "Oświadczenia o wysokości dochodu osoby ubiegającej się, jej małżonka oraz zstępnych przed wstępnymi",
      hint: "Oświadczenie każdej z tych osób, podpisane przez nią; gdy opłatę ponosi przedstawiciel ustawowy małoletniego — także oświadczenie o dochodzie małoletniego.",
      visibleWhen: NOT_COURT,
      required: true,
      multiple: true,
    },
    {
      code: "decyzjaZasilekStaly",
      label: "Decyzja o przyznaniu zasiłku stałego",
      visibleWhen: { all: [NOT_COURT, { field: "dochodOsoby", includes: "zasilekStaly" }] },
      required: true,
    },
    {
      code: "decyzjaEmerytura",
      label: "Decyzja organu rentowego (ZUS, KRUS) ustalająca wysokość emerytury lub renty",
      visibleWhen: { all: [NOT_COURT, { field: "dochodOsoby", includes: "emerytura" }] },
      required: true,
    },
    {
      code: "zgodaPotracenie",
      label: "Pisemna zgoda osoby kierowanej na ponoszenie opłaty za pobyt i jej potrącanie z zasiłku stałego lub przez organ rentowy",
      hint: "Zgodę podpisuje osoba kierowana (albo jej przedstawiciel ustawowy), a nie pełnomocnik.",
      visibleWhen: { all: [NOT_COURT, ON_BEHALF, INCOME_DECISION] },
      required: true,
    },
    {
      code: "zaswiadczenieKombatanta",
      label: "Zaświadczenie potwierdzające uprawnienia kombatanckie (art. 22 ustawy o kombatantach)",
      visibleWhen: { all: [NOT_COURT, { field: "kombatant", equals: "tak" }] },
      required: true,
    },
    {
      code: "zaswiadczeniaLekarskie",
      label: "Zaświadczenia lekarskie i dokumentacja medyczna",
      hint: "Nieobowiązkowo — ośrodki zwykle proszą o zaświadczenie lekarskie o stanie zdrowia na swoim druku; przy chorobie psychicznej także o zaświadczenie lekarza psychiatry, przy niepełnosprawności intelektualnej — psychologa.",
      multiple: true,
    },
    {
      code: "orzeczenieNiepelnosprawnosci",
      label: "Orzeczenie o niepełnosprawności lub o stopniu niepełnosprawności",
      hint: "Nieobowiązkowo — jeśli osoba je posiada.",
    },
    otherAttachments(),
  ],
};

export default C2;
